<script setup lang="ts">
import {createNoise2D} from "simplex-noise";
import Stats from "stats.js";
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  PerspectiveCamera,
  Points,
  Scene,
  ShaderMaterial,
  WebGLRenderer,
} from "three"; // TODO : Make sure this component is it's own bundle

const targetElement = ref<HTMLElement | null>(null);
const simplexNoise2D = createNoise2D();
const mapNoise = (x: number, y: number) => simplexNoise2D(x, y);

// const stats =  new Stats();
// stats.showPanel(0);
// document.body.appendChild(stats.dom);

const setupAnimation = (targetElement: HTMLElement | null) => {
  if (!targetElement) return;

  const scene = new Scene();
  const camera = new PerspectiveCamera(
    75,
    window.innerWidth / targetElement.clientHeight,
    0.1,
    1000,
  );
  const renderer = new WebGLRenderer();
  renderer.setPixelRatio(window.devicePixelRatio); // Support retina displays

  const width = window.innerWidth;
  const height = targetElement.clientHeight;
  renderer.setSize(width, height);

  // Append the renderer's DOM element to the #target element
  targetElement.appendChild(renderer.domElement);

  // Adjust the camera aspect ratio and update the projection matrix
  camera.aspect = width / height;
  camera.position.set(0, -20, 7);
  camera.lookAt(0, 0, 0);

  camera.updateProjectionMatrix();

  // Terrain Geometry
  const terrainWidth = 80;
  const terrainHeight = 30;

  // Create Particle System
  // Configurable spacing between particles
  const spacingX = 0.5; // Set horizontal spacing between particles
  const spacingY = 0.25; // Set vertical spacing between particles

  // Calculate grid size based on spacing
  const gridCountX = Math.floor(terrainWidth / spacingX);
  const gridCountY = Math.floor(terrainHeight / spacingY);

  // Create Particle System
  const particles = new BufferGeometry();
  const positions = [];

  // Generate particles based on spacing, determining number by terrain dimensions
  for (let i = 0; i < gridCountX; i++) {
    for (let j = 0; j < gridCountY; j++) {
      const x = -terrainWidth / 2 + i * spacingX;
      const y = -terrainHeight / 2 + j * spacingY;
      const z = mapNoise(x * 0.1, y * 0.1) * 2; // Initial Z position based on noise
      positions.push(x, y, z);
    }
  }
  particles.setAttribute("position", new Float32BufferAttribute(positions, 3));

  // Custom Shader Material for Particles with Y Position-Based Fading (configurable fade-in and fade-out)
  const particleShaderMaterialX = new ShaderMaterial({
    uniforms: {
      minY: {value: -terrainHeight / 2}, // Starting Y position (front of plane)
      maxY: {value: terrainHeight / 2}, // Ending Y position (back of plane)
      fadeInRatio: {value: 0.15}, // Percentage of distance for fade-in
      fadeOutRatio: {value: 0.5}, // Percentage of distance for fade-out
      minZ: {value: -2.0}, // Minimum Z height for opacity adjustment
      maxZ: {value: 2.0},
      particleColor: {value: new Color(0xcccccc)}, // Default particle color
    },
    vertexShader: `
    varying float vYPosition;
		varying float vZPosition;
    void main() {
      vYPosition = position.y; // Pass Y position to fragment shader
			vZPosition = position.z;
      gl_PointSize = 2.0; // Set particle size (can be adjusted as needed)
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
    fragmentShader: `
    uniform float minY;
    uniform float maxY;
    uniform float fadeInRatio;
    uniform float fadeOutRatio;
  	uniform float minZ;
    uniform float maxZ;
    uniform vec3 particleColor;
    varying float vYPosition;
    varying float vZPosition;

    void main() {
      // Calculate fade-in end position based on fadeInRatio (10% from minY)
      float fadeInEnd = mix(minY, maxY, fadeInRatio);

      // Calculate fade-out start position based on fadeOutRatio (90% from minY)
      float fadeOutStart = mix(minY, maxY, 1.0 - fadeOutRatio);

      // Apply fade-in effect: fades in from minY to fadeInEnd
      float fadeInOpacity = smoothstep(minY, fadeInEnd, vYPosition);

      // Apply fade-out effect: fades out from fadeOutStart to maxY
      float fadeOutOpacity = smoothstep(fadeOutStart, maxY, vYPosition);

      // Combine fade-in and fade-out effects by multiplication
      float yOpacity = fadeInOpacity * (1.0 - fadeOutOpacity);

      // Calculate Z-based opacity to add depth effect
      float zOpacity = smoothstep(minZ, maxZ, vZPosition); // Fade based on z height
      float finalOpacity = yOpacity * zOpacity; // Combine Y and Z opacities

			// Calculate distance from the center of the point to create a circular mask
      vec2 coord = gl_PointCoord - vec2(0.5);
      float dist = length(coord);
      float circleMask = smoothstep(0.5, 0.48, dist); // Soft edge for circular mask

      gl_FragColor = vec4(particleColor, finalOpacity * circleMask); // Circular particles with fade effect
    }
  `,
    transparent: true,
  });

  const particleSystem = new Points(particles, particleShaderMaterialX);
  scene.add(particleSystem);

  // Store original x positions outside animate
  const originalX: number[] = [];
  for (let i = 0; i < particles.attributes.position.array.length; i += 3) {
    originalX.push(particles.attributes.position.array[i]); // Store the original x position
  }

  // Animation Loop
  function animate() {
    requestAnimationFrame(animate);
    // stats.begin();

    const time = performance.now() * 0.001 * -2;

    // Update particle positions
    const positions = particles.attributes.position.array;
    for (let i = 0; i < positions.length; i += 3) {
      let x = positions[i];
      const baseX = originalX[i / 3]; // Reference original x position
      let y = positions[i + 1];

      // Move particles in the Y direction (front to back)
      y += 0.0275; // Todo: Check on laptop vs external screen
      if (y > terrainHeight / 2) {
        y = -terrainHeight / 2; // Reset position to the front
      } else if (y < -terrainHeight / 2) {
        y = terrainHeight / 2; // Reset position to the back
      }
      // Update Z position to match terrain's noise height
      const z = mapNoise(baseX * 0.1, (y + time * 2) * 0.1) * 2;
      // Calculate gradient to determine contour direction
      const delta = 0.1; // Small step for gradient calculation
      const zLeft = mapNoise((baseX - delta) * 0.1, (y + time * 2) * 0.1) * 2;
      const zRight = mapNoise((baseX + delta) * 0.1, (y + time * 2) * 0.1) * 2;

      const gradient = zRight - zLeft; // Positive if slope rises to the right, negative if to the left

      // Calculate xOffset based on gradient direction and contour effect
      const xOffset = gradient * 3; // Adjust 0.5 for contour strength

      positions[i] = baseX + xOffset; // Apply intelligent offset based on gradient

      positions[i + 1] = y;
      positions[i + 2] = z;
    }
    particles.attributes.position.needsUpdate = true;

    renderer.render(scene, camera);

    // stats.end();
  }

  animate();

  // Handle window resize
  window.addEventListener("resize", () => {
    // Get the new size of the target element
    const width = window.innerWidth;
    const height = targetElement.clientHeight;

    // Update the camera aspect ratio and the renderer size
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  });
};
onMounted(() => {
  nextTick(() => {
    setupAnimation(targetElement.value);
  });
});
</script>

<template>
  <div ref="targetElement"></div>
</template>

<style scoped lang="scss"></style>
